/**
 * Pure layout solver for the Map Dashboard zone callouts.
 *
 * Every zone gets a small card placed OUTSIDE all zone polygons (so it never covers the zone it describes or a
 * neighbour) and joined to a dot inside its own zone by a thin leader line. Everything is in map-container pixels;
 * the caller does the Leaflet projection. No React or Leaflet imports, so it can be exercised on its own.
 *
 * Placement is a small constraint problem: candidate slots on rings around the venue bounding box, a cost per slot
 * (leader length, crossing other zones / panels), pairwise penalties (cards overlapping, leaders crossing), solved
 * with seeded iterated conditional modes so the result is deterministic for a given input.
 */

export interface Pt { x: number; y: number }
export interface Rect { x: number; y: number; w: number; h: number }
export type CardSize = 'full' | 'compact';

export interface CardZoneInput {
  id: string;
  /** Outer ring of the zone polygon in container px (open ring, no repeated closing vertex). */
  ring: Pt[];
  /** Where the leader ends (a point inside the zone), container px. */
  anchor: Pt;
  sizes: Record<CardSize, { w: number; h: number }>;
}

export interface CardLayoutInput {
  zones: CardZoneInput[];
  /** The part of the container cards may occupy. */
  safe: Rect;
  /** Panels floating over the map (container px). Cards and leaders keep clear of them. */
  obstacles: Rect[];
  /** Rings of zones that get no card (their tag is hidden) but must still be kept clear. Container px. */
  blockers?: Pt[][];
  /** Slot keys from the previous layout; matching slots get a small bonus so cards don't wander. */
  previous?: Record<string, string>;
}

export interface CardPlacement {
  zoneId: string;
  /** callout = outside the venue with a leader; tag = small label inside the zone (no room outside). */
  mode: 'callout' | 'tag';
  size: CardSize;
  card: Rect;
  anchor: Pt;
  /** Where the leader leaves the card; null for tags. */
  attach: Pt | null;
  slotKey: string;
}

// ── Tunables ──────────────────────────────────────────────────────────────────

const EDGE_GAPS = [10, 28, 56];
const POSITION_STEP = 16;
const OBSTACLE_INFLATE = 6;
const POLYGON_INFLATE = 2;
const CARD_CLEARANCE = 6;
const MAX_CANDIDATES = 40;
const NMS_BUCKET = 28;
const RESTARTS = 8;
const SWEEPS = 6;
const TAG_INSET = 10;
const MAX_LEADER_SHARE = 0.55;

const W_RING = 0.6;
const W_CROSS_ZONE_PER_PX = 8;
const W_CROSS_ZONE_FLAT = 300;
const W_CROSS_OBSTACLE = 200;
const W_STICKY = -40;
const PAIR_CARD_OVERLAP = 5000;
const PAIR_LEADER_CROSS = 700;
const PAIR_LEADER_THROUGH_CARD = 900;

// ── Geometry ──────────────────────────────────────────────────────────────────

const inflate = (r: Rect, by: number): Rect => ({ x: r.x - by, y: r.y - by, w: r.w + 2 * by, h: r.h + 2 * by });
const rectsOverlap = (a: Rect, b: Rect): boolean =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
const insideRect = (r: Rect, p: Pt): boolean => p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
const containsRect = (outer: Rect, inner: Rect): boolean =>
  inner.x >= outer.x && inner.y >= outer.y && inner.x + inner.w <= outer.x + outer.w && inner.y + inner.h <= outer.y + outer.h;

function boundsOf(points: Pt[]): Rect {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function pointInPolygon(p: Pt, ring: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

function segmentsIntersect(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o = (p: Pt, q: Pt, r: Pt) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const o1 = o(a, b, c), o2 = o(a, b, d), o3 = o(c, d, a), o4 = o(c, d, b);
  return ((o1 > 0) !== (o2 > 0)) && ((o3 > 0) !== (o4 > 0));
}

function segmentHitsRect(a: Pt, b: Pt, r: Rect): boolean {
  if (insideRect(r, a) || insideRect(r, b)) return true;
  const tl = { x: r.x, y: r.y }, tr = { x: r.x + r.w, y: r.y };
  const bl = { x: r.x, y: r.y + r.h }, br = { x: r.x + r.w, y: r.y + r.h };
  return segmentsIntersect(a, b, tl, tr) || segmentsIntersect(a, b, tr, br)
    || segmentsIntersect(a, b, br, bl) || segmentsIntersect(a, b, bl, tl);
}

const ringBounds = new WeakMap<Pt[], Rect>();
function boundsOfCached(ring: Pt[]): Rect {
  let b = ringBounds.get(ring);
  if (!b) { b = boundsOf(ring); ringBounds.set(ring, b); }
  return b;
}

function rectHitsPolygon(r: Rect, ring: Pt[]): boolean {
  if (!rectsOverlap(r, boundsOfCached(ring))) return false;
  if (ring.some(p => insideRect(r, p))) return true;
  const corners = [{ x: r.x, y: r.y }, { x: r.x + r.w, y: r.y }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h }];
  if (corners.some(c => pointInPolygon(c, ring))) return true;
  for (let i = 0; i < ring.length; i++) {
    if (segmentHitsRect(ring[i], ring[(i + 1) % ring.length], r)) return true;
  }
  return false;
}

function nearestPointOnRect(r: Rect, p: Pt): Pt {
  return { x: Math.min(Math.max(p.x, r.x), r.x + r.w), y: Math.min(Math.max(p.y, r.y), r.y + r.h) };
}

const dist = (a: Pt, b: Pt): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Length of segment a-b that lies inside `ring`, sampled every ~4 px. */
function lengthInside(a: Pt, b: Pt, ring: Pt[]): number {
  const len = dist(a, b);
  if (len === 0) return 0;
  const steps = Math.max(1, Math.ceil(len / 4));
  let hits = 0;
  for (let i = 0; i < steps; i++) {
    const t = (i + 0.5) / steps;
    if (pointInPolygon({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, ring)) hits++;
  }
  return (hits / steps) * len;
}

function hashString(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i++) { h ^= value.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

function seededRandom(seed: number): () => number {
  let s = seed || 1;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; };
}

/** Every zone outline cards must stay off: the carded zones plus any blockers. */
const allRings = (input: CardLayoutInput): Pt[][] => [...input.zones.map(z => z.ring), ...(input.blockers ?? [])];

// ── Candidate generation ──────────────────────────────────────────────────────

interface Candidate {
  card: Rect;
  attach: Pt;
  unary: number;
  key: string;
}

function candidatesFor(
  zone: CardZoneInput,
  size: CardSize,
  venue: Rect,
  input: CardLayoutInput,
  maxLeader: number,
): Candidate[] {
  const { w, h } = zone.sizes[size];
  const obstacles = input.obstacles.map(o => inflate(o, OBSTACLE_INFLATE));
  const others = input.zones.filter(z => z.id !== zone.id);
  const raw: Array<{ card: Rect; key: string; bucket: string; gap: number }> = [];

  for (const gap of EDGE_GAPS) {
    const spans: Array<[string, Rect, 'x' | 'y']> = [
      ['N', { x: venue.x - w - gap, y: venue.y - gap - h, w: venue.w + 2 * (w + gap), h: 0 }, 'x'],
      ['S', { x: venue.x - w - gap, y: venue.y + venue.h + gap, w: venue.w + 2 * (w + gap), h: 0 }, 'x'],
      ['W', { x: venue.x - gap - w, y: venue.y - h - gap, w: 0, h: venue.h + 2 * (h + gap) }, 'y'],
      ['E', { x: venue.x + venue.w + gap, y: venue.y - h - gap, w: 0, h: venue.h + 2 * (h + gap) }, 'y'],
    ];
    for (const [side, span, axis] of spans) {
      const length = axis === 'x' ? span.w : span.h;
      for (let off = 0; off <= length; off += POSITION_STEP) {
        const card: Rect = axis === 'x'
          ? { x: span.x + off, y: span.y, w, h }
          : { x: span.x, y: span.y + off, w, h };
        const pos = Math.round(off / NMS_BUCKET);
        raw.push({ card, key: `${side}|${gap}|${pos}`, bucket: `${side}|${gap}|${pos}`, gap });
      }
    }
  }

  const out: Candidate[] = [];
  for (const c of raw) {
    if (!containsRect(input.safe, c.card)) continue;
    const padded = inflate(c.card, 3);
    if (obstacles.some(o => rectsOverlap(padded, o))) continue;
    // No polygon test needed: every slot sits at least EDGE_GAPS[0] px outside the box that contains every outline.

    const attach = nearestPointOnRect(c.card, zone.anchor);
    const leader = dist(attach, zone.anchor);
    if (leader > maxLeader) continue;

    let crossZone = 0;
    for (const other of others) crossZone += lengthInside(attach, zone.anchor, other.ring);
    let unary = leader + W_RING * c.gap;
    if (crossZone > 0) unary += W_CROSS_ZONE_FLAT + W_CROSS_ZONE_PER_PX * crossZone;
    if (obstacles.some(o => segmentHitsRect(attach, zone.anchor, o))) unary += W_CROSS_OBSTACLE;
    if (input.previous?.[zone.id] === c.key) unary += W_STICKY;
    out.push({ card: c.card, attach, unary, key: c.key });
  }

  // Keep the cheapest candidate per (side, gap, 28px bucket) so the shortlist is varied rather than 40 neighbours.
  out.sort((a, b) => a.unary - b.unary);
  const seen = new Set<string>();
  const shortlist: Candidate[] = [];
  for (const cand of out) {
    if (seen.has(cand.key)) continue;
    seen.add(cand.key);
    shortlist.push(cand);
    if (shortlist.length >= MAX_CANDIDATES) break;
  }
  return shortlist;
}

function pairCost(a: Candidate, aAnchor: Pt, b: Candidate, bAnchor: Pt): number {
  let cost = 0;
  if (rectsOverlap(inflate(a.card, CARD_CLEARANCE / 2), inflate(b.card, CARD_CLEARANCE / 2))) cost += PAIR_CARD_OVERLAP;
  if (segmentsIntersect(a.attach, aAnchor, b.attach, bAnchor)) cost += PAIR_LEADER_CROSS;
  if (segmentHitsRect(a.attach, aAnchor, inflate(b.card, 3))) cost += PAIR_LEADER_THROUGH_CARD;
  if (segmentHitsRect(b.attach, bAnchor, inflate(a.card, 3))) cost += PAIR_LEADER_THROUGH_CARD;
  return cost;
}

interface LevelSolution {
  chosen: Array<Candidate | null>;
  total: number;
  /** How many chosen cards overlap another chosen card. Zero means the layout is clean. */
  overlaps: number;
}

/** True when the margins around the venue are big enough for at least one card of this size. */
function hasRoomOutside(input: CardLayoutInput, venue: Rect, size: CardSize): boolean {
  const w = Math.min(...input.zones.map(z => z.sizes[size].w));
  const h = Math.min(...input.zones.map(z => z.sizes[size].h));
  const { safe } = input;
  return venue.x - safe.x >= w + 10 || safe.x + safe.w - (venue.x + venue.w) >= w + 10
    || venue.y - safe.y >= h + 10 || safe.y + safe.h - (venue.y + venue.h) >= h + 10;
}

function solveLevel(input: CardLayoutInput, size: CardSize): LevelSolution {
  const { zones } = input;
  const venue = boundsOf(allRings(input).flat());
  // Zoomed in so far that nothing fits outside the venue: skip the (expensive) search, the caller falls back to tags.
  if (!hasRoomOutside(input, venue, size)) return { chosen: zones.map(() => null), total: 1e6, overlaps: 0 };
  const maxLeader = MAX_LEADER_SHARE * Math.min(input.safe.w, input.safe.h);
  const cands = zones.map(z => candidatesFor(z, size, venue, input, maxLeader));

  const order = zones.map((_, i) => i).sort((a, b) => cands[a].length - cands[b].length);
  const rand = seededRandom(hashString(zones.map(z => z.id).join('|')));
  let best: LevelSolution | null = null;

  const costOf = (zi: number, cand: Candidate, chosen: Array<Candidate | null>): number => {
    let c = cand.unary;
    chosen.forEach((other, oi) => {
      if (!other || oi === zi) return;
      c += pairCost(cand, zones[zi].anchor, other, zones[oi].anchor);
    });
    return c;
  };

  for (let restart = 0; restart < RESTARTS; restart++) {
    const seq = order.slice();
    if (restart > 0) for (let i = seq.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [seq[i], seq[j]] = [seq[j], seq[i]]; }
    const chosen: Array<Candidate | null> = zones.map(() => null);

    for (const zi of seq) {
      let pick: Candidate | null = null, pickCost = Infinity;
      for (const cand of cands[zi]) { const c = costOf(zi, cand, chosen); if (c < pickCost) { pickCost = c; pick = cand; } }
      chosen[zi] = pick;
    }
    for (let sweep = 0; sweep < SWEEPS; sweep++) {
      let changed = false;
      for (const zi of seq) {
        let pick = chosen[zi], pickCost = pick ? costOf(zi, pick, chosen) : Infinity;
        for (const cand of cands[zi]) { const c = costOf(zi, cand, chosen); if (c < pickCost - 1e-6) { pickCost = c; pick = cand; changed = true; } }
        chosen[zi] = pick;
      }
      if (!changed) break;
    }

    let total = 0, overlaps = 0;
    chosen.forEach((cand, zi) => {
      if (!cand) { total += 1e6; return; }
      total += cand.unary;
      chosen.forEach((other, oi) => {
        if (!other || oi <= zi) return;
        total += pairCost(cand, zones[zi].anchor, other, zones[oi].anchor);
        if (rectsOverlap(inflate(cand.card, CARD_CLEARANCE / 2), inflate(other.card, CARD_CLEARANCE / 2))) overlaps++;
      });
    });
    if (!best || total < best.total) best = { chosen, total, overlaps };
  }
  return best ?? { chosen: zones.map(() => null), total: 1e6, overlaps: 0 };
}

// ── Tags (no room outside) ────────────────────────────────────────────────────

/** In-zone label for when there is no room outside. Null when even that would collide: the zone keeps just its reticle. */
function placeTag(zone: CardZoneInput, size: CardSize, taken: Rect[], input: CardLayoutInput): Rect | null {
  const { w, h } = zone.sizes[size];
  const box = boundsOf(zone.ring);
  const visible: Rect = {
    x: Math.max(box.x, input.safe.x),
    y: Math.max(box.y, input.safe.y),
    w: Math.min(box.x + box.w, input.safe.x + input.safe.w) - Math.max(box.x, input.safe.x),
    h: Math.min(box.y + box.h, input.safe.y + input.safe.h) - Math.max(box.y, input.safe.y),
  };
  const corners: Pt[] = [
    { x: visible.x + TAG_INSET, y: visible.y + TAG_INSET },
    { x: visible.x + visible.w - TAG_INSET - w, y: visible.y + TAG_INSET },
    { x: visible.x + TAG_INSET, y: visible.y + visible.h - TAG_INSET - h },
    { x: visible.x + visible.w - TAG_INSET - w, y: visible.y + visible.h - TAG_INSET - h },
  ];
  const obstacles = input.obstacles.map(o => inflate(o, OBSTACLE_INFLATE));
  for (const c of corners) {
    const card: Rect = { x: c.x, y: c.y, w, h };
    if (!containsRect(input.safe, card)) continue;
    // The tag stands for this zone, so it must sit wholly inside it.
    const corners4 = [{ x: card.x, y: card.y }, { x: card.x + w, y: card.y }, { x: card.x + w, y: card.y + h }, { x: card.x, y: card.y + h }];
    if (!corners4.every(p => pointInPolygon(p, zone.ring))) continue;
    if (obstacles.some(o => rectsOverlap(card, o))) continue;
    if (taken.some(t => rectsOverlap(inflate(card, CARD_CLEARANCE / 2), inflate(t, CARD_CLEARANCE / 2)))) continue;
    return card;
  }
  return null;
}

// ── Gutter layout (preferred) ─────────────────────────────────────────────────

const GUTTER_GAP_X = 28;
const GUTTER_GAP_Y = 8;

/**
 * Two tidy columns of tags, one each side of the venue, each tag at the height of its own zone. Leaders then run
 * mostly horizontally into the venue, like air-traffic data-block leaders, instead of fanning out diagonally.
 * Returns null when the columns do not fit (narrow window, heavy zoom), so the caller can try something else.
 */
function solveGutterLevel(input: CardLayoutInput, size: CardSize): CardPlacement[] | null {
  const { zones } = input;
  const n = zones.length;
  if (n === 0) return [];
  const venue = boundsOf(allRings(input).flat());
  const rings = allRings(input);
  const obstacles = input.obstacles.map(o => inflate(o, OBSTACLE_INFLATE));

  /** Stacks one column of tags beside the venue, each as close as it can get to its own zone's height. */
  const stack = (items: CardZoneInput[], side: 'L' | 'R'): CardPlacement[] | null => {
    if (items.length === 0) return [];
    const { safe } = input;
    const sorted = items.slice().sort((a, b) => a.anchor.y - b.anchor.y);
    const tops: number[] = [];
    sorted.forEach((z, i) => {
      const want = z.anchor.y - z.sizes[size].h / 2;
      const floor = i === 0 ? safe.y : tops[i - 1] + sorted[i - 1].sizes[size].h + GUTTER_GAP_Y;
      tops.push(Math.max(want, floor));
    });
    const last = sorted.length - 1;
    if (tops[last] + sorted[last].sizes[size].h > safe.y + safe.h) {
      for (let i = last; i >= 0; i--) {
        const limit = i === last
          ? safe.y + safe.h - sorted[i].sizes[size].h
          : tops[i + 1] - GUTTER_GAP_Y - sorted[i].sizes[size].h;
        tops[i] = Math.min(tops[i], limit);
      }
    }
    const out: CardPlacement[] = [];
    for (let i = 0; i < sorted.length; i++) {
      const z = sorted[i];
      const { w, h } = z.sizes[size];
      const x = side === 'L' ? venue.x - GUTTER_GAP_X - w : venue.x + venue.w + GUTTER_GAP_X;
      const card: Rect = { x, y: tops[i], w, h };
      if (!containsRect(safe, card)) return null;
      if (obstacles.some(o => rectsOverlap(inflate(card, 3), o))) return null;
      if (rings.some(ring => rectHitsPolygon(inflate(card, POLYGON_INFLATE), ring))) return null;
      const attach: Pt = { x: side === 'L' ? card.x + card.w : card.x, y: card.y + card.h / 2 };
      out.push({ zoneId: z.id, mode: 'callout', size, card, anchor: z.anchor, attach, slotKey: `G${side}` });
    }
    return out;
  };

  // Which zone goes to which column is decided by cost: short leaders, no crossing leaders, and a small bonus for
  // keeping a zone on the side it had before. Up to ten zones can be tried exhaustively (2^n splits).
  const splits: number[] = [];
  if (n <= 10) {
    for (let mask = 0; mask < (1 << n); mask++) {
      let left = 0;
      for (let i = 0; i < n; i++) if (mask & (1 << i)) left++;
      if (Math.abs(2 * left - n) <= 2) splits.push(mask);
    }
  } else {
    const byX = zones.map((_, i) => i).sort((a, b) => zones[a].anchor.x - zones[b].anchor.x);
    splits.push(byX.slice(0, Math.floor(n / 2)).reduce((m, i) => m | (1 << (i % 30)), 0));
  }

  let best: { placements: CardPlacement[]; cost: number } | null = null;
  for (const mask of splits) {
    const left: CardZoneInput[] = [], right: CardZoneInput[] = [];
    zones.forEach((z, i) => ((mask & (1 << (i % 30))) ? left : right).push(z));
    const l = stack(left, 'L');
    if (!l) continue;
    const r = stack(right, 'R');
    if (!r) continue;
    const placements = [...l, ...r];

    let cost = 0;
    for (const p of placements) {
      cost += dist(p.attach as Pt, p.anchor);
      if (input.previous?.[p.zoneId] === p.slotKey) cost += W_STICKY;
    }
    for (let i = 0; i < placements.length; i++) {
      for (let j = i + 1; j < placements.length; j++) {
        const a = placements[i], b = placements[j];
        if (segmentsIntersect(a.attach as Pt, a.anchor, b.attach as Pt, b.anchor)) cost += PAIR_LEADER_CROSS;
      }
    }
    if (!best || cost < best.cost) best = { placements, cost };
  }
  return best ? best.placements : null;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Solves the layout. Tries full-size cards first, then compact ones; any zone with no free slot outside the venue
 * (heavily zoomed in, tiny window) falls back to a small in-zone tag.
 */
export function solveCardLayout(input: CardLayoutInput): CardPlacement[] {
  const zones = input.zones.filter(z => z.ring.length >= 3 && boundsOverlapsSafe(z.ring, input.safe));
  if (zones.length === 0) return [];
  // Off-screen outlines must not stretch the venue box or veto slots, so only visible blockers count.
  const blockers = (input.blockers ?? []).filter(r => r.length >= 3 && boundsOverlapsSafe(r, input.safe));
  const scoped: CardLayoutInput = { ...input, zones, blockers };

  for (const level of ['full', 'compact'] as CardSize[]) {
    const gutters = solveGutterLevel(scoped, level);
    if (gutters) return gutters;
  }

  let size: CardSize = 'full';
  let solution = solveLevel(scoped, 'full');
  if (solution.chosen.some(c => !c) || solution.overlaps > 0) {
    size = 'compact';
    solution = solveLevel(scoped, 'compact');
    // Still colliding at the smallest size: better a few clean in-zone tags (or just reticles) than overlapping cards.
    if (solution.overlaps > 0) solution = { chosen: zones.map(() => null), total: 0, overlaps: 0 };
  }

  const placements: CardPlacement[] = [];
  const taken = solution.chosen.filter((c): c is Candidate => Boolean(c)).map(c => c.card);
  solution.chosen.forEach((cand, i) => {
    const zone = zones[i];
    if (cand) {
      placements.push({ zoneId: zone.id, mode: 'callout', size, card: cand.card, anchor: zone.anchor, attach: cand.attach, slotKey: cand.key });
    } else {
      const card = placeTag(zone, 'compact', taken, scoped);
      if (!card) return;
      taken.push(card);
      placements.push({ zoneId: zone.id, mode: 'tag', size: 'compact', card, anchor: zone.anchor, attach: null, slotKey: 'tag' });
    }
  });
  return placements;
}

function boundsOverlapsSafe(ring: Pt[], safe: Rect): boolean {
  return rectsOverlap(boundsOf(ring), safe);
}
